import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import {
  createTransform,
  PersistConfig,
  persistReducer,
  persistStore,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import storageSession from "redux-persist/lib/storage/session";

import adminReducer from "./admin/admin.slice";
import appReducer from "./app/app.slice";
import blogsReducer from "./blogs/blogs.slice";
import calculatorReducer from "./calculator/calculator.slice";
import campaignReducer from "./campaign/campaign.slice";
import dashboardReduce from "./dashboard/dashboard.slice";
import entryReducer from "./entry/entry.slice";
import footerState from "./footer-daily-stats/footer-daily-stats.slice";
import formReducer from "./forms/form.slice";
import integrationReducer from "./integration/integration.slice";
import kanbanSlice from "./kanban/kanban.slice";
import paymentReducer from "./payment/payment.slice";
import permissionReducer from "./permission/permission.slice";
import settingsReducer from "./settings/settings.slice";
import taskReducer from "./task/task.slice";
import templateReducer from "./template/template.slice";
import thresholdReducer from "./threshold/threshold.slice";
import userReducer from "./user/user.slice";
import widgetReducer from "./widget/widget.slice";
import reportReducer from "./report/report.slice";
import moduleReducer from "./module/module.slice";
import chatbotReducer from "./chatbot/chatbot.slice";
import orgChatReducer from "./chat/orgChat.slice";
import tagReducer from "./tag/tag.slice";
import { runSessionBoot } from "../sessionBoot";
import { isImpersonationTab, setStoreRef } from "./store-utils";

// Must happen before the persistor below is built: it decides whether this
// tab's persisted state is safe to rehydrate at all.
runSessionBoot();

const impersonationTab = isImpersonationTab();

const persistConfig: PersistConfig<ReturnType<typeof reducers>> = {
  key: impersonationTab ? "impersonation" : "root",
  storage: impersonationTab ? storageSession : storage,
  version: 0,
  // The report is a snapshot of a time window, and it is cheap to refetch.
  // Rehydrating it showed yesterday's figures under a filter that had reset
  // itself to today; rehydrating `reportLoading` stranded the page on a
  // spinner it could never clear.
  // Team chat holds decrypted message text, which should not sit in
  // localStorage, and it is refetched on open anyway.
  blacklist: ["report", "orgChat"],
  // The inbox is a window onto a server-side list that is fetched in pages on
  // every load. Persisted, the pages from an earlier session sat in the list as
  // chats the server may no longer have, and nothing replaced them.
  transforms: [
    createTransform(
      (integration: any) => ({
        ...integration,
        whatsappIntegrationMetaChatsList: [],
        whatsappIntegrationMetaChatsListLoading: false,
        whatsappIntegrationMetaChatsListLoaded: false,
        whatsappIntegrationMetaChatsListNextCursor: undefined,
        whatsappIntegrationMetaChatsListReachedAt: null,
        whatsappIntegrationMetaChatsListLoadingMore: false,
        whatsappIntegrationMetaChatsElsewhere: { unread: 0, needsAttention: 0 },
        whatsappIntegrationMetaChatsView: null,
      }),
      (integration: any) => integration,
      { whitelist: ["integration"] },
    ),
  ],
};

const reducers = combineReducers({
  user: userReducer,
  form: formReducer,
  template: templateReducer,
  entry: entryReducer,
  dashBaord: dashboardReduce,
  kanban: kanbanSlice,
  widget: widgetReducer,
  app: appReducer,
  task: taskReducer,
  blogs: blogsReducer,
  threshold: thresholdReducer,
  permission: permissionReducer,
  admin: adminReducer,
  calculator: calculatorReducer,
  integration: integrationReducer,
  footer: footerState,
  settings: settingsReducer,
  payment: paymentReducer,
  campaign: campaignReducer,
  report: reportReducer,
  module: moduleReducer,
  chatbot: chatbotReducer,
  orgChat: orgChatReducer,
  tag: tagReducer,
});

const persistedReducer = persistReducer(persistConfig, reducers);

const _store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }),
});

// Registered so the axios layer can read the current identity without closing
// an import cycle. See store-utils.ts.
setStoreRef(_store);

export type AppDispatch = typeof _store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();

export type RootState = ReturnType<typeof _store.getState>;

export const useAppSelector = useSelector.withTypes<RootState>();

const persistedStore = persistStore(_store);
const store = { store: _store, persistor: persistedStore };

export default store;
